import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { RootState } from '../index'

const baseQuery = fetchBaseQuery({
  baseUrl: '/api/v1/admin/expansion-deliverables',
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.token
    if (token) {
      headers.set('authorization', `Bearer ${token}`)
    }
    headers.set('Content-Type', 'application/json')
    headers.set('Accept', 'application/json')
    return headers
  },
})

export interface ExpansionDeliverable {
  id: number
  uuid: string
  expansionReasons: {
    moreEngineers: boolean
    newTeams: boolean
    executives: boolean
    stakeholders: boolean
    other: boolean
  }
  otherDescription?: string
  additionalUsers: number
  status: 'pending' | 'resolved' | 'cancelled'
  createdAt: string
  resolvedAt?: string
  seatsAdded?: number
  account: {
    uuid: string
    purchasedSeats: number
    availableSeats: number
    totalSeats: number
    actualUsedSeats: number
  }
  organization: {
    uuid: string
    name: string
  }
  user: {
    uuid: string
    name: string
    email: string
  }
  resolvedBy?: {
    uuid: string
    name: string
    email: string
  }
}

export const expansionDeliverablesApi = createApi({
  reducerPath: 'expansionDeliverablesApi',
  baseQuery,
  tagTypes: ['ExpansionDeliverables'],
  endpoints: (builder) => ({
    getExpansionDeliverables: builder.query<ExpansionDeliverable[], { status?: string }>({
      query: ({ status }) => {
        const params = new URLSearchParams()
        if (status) {
          params.append('status', status)
        }
        return {
          url: `/?${params.toString()}`,
          method: 'GET',
        }
      },
      transformResponse: (response: any) => {
        if (!response.success) {
          throw new Error(response.message || 'Failed to fetch expansion deliverables')
        }
        return response.data || []
      },
      providesTags: ['ExpansionDeliverables'],
    }),
    resolveExpansionDeliverable: builder.mutation<
      { success: boolean; message: string },
      { deliverableId: number; seatsToAdd: number; invoicePaid: boolean; invoiceDescription?: string }
    >({
      query: ({ deliverableId, seatsToAdd, invoicePaid, invoiceDescription }) => ({
        url: `/${deliverableId}/resolve`,
        method: 'PUT',
        body: { seatsToAdd, invoicePaid, invoiceDescription },
      }),
      transformResponse: (response: any) => {
        if (!response.success) {
          throw new Error(response.message || 'Failed to resolve expansion deliverable')
        }
        return response
      },
      invalidatesTags: ['ExpansionDeliverables'],
    }),
  }),
})

export const {
  useGetExpansionDeliverablesQuery,
  useResolveExpansionDeliverableMutation,
} = expansionDeliverablesApi
